import {
    GetUserDocument,
} from '@redviking/argonaut-core-ui/types/db';
import { userToString } from '@redviking/argonaut-core-ui/src/util';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { accessor } from '../store';
import { Route } from 'vue-router';
import { RouterViewSlider } from '../components/RouterViewSlider';
import { RouteEntityDetail } from '@redviking/argonaut-core-ui/src/components/EntityDetail';
import { messages } from 'src/i18n/i18n';
import { isAdmin } from './user.validations';
import {
    entityDetailRoute,
} from '@redviking/argonaut-core-ui/src/components/EntityDetail/entity-route-util';
import { entityPageBannerTitle } from 'src/util/formatting';
import { v4 as uuidv4 } from 'uuid';
import { EntityTypeMap } from 'types';
import { createUserEntity, saveUserEntity, userFragmentToEntityType } from './user.entity';
import { validateRoleSelectAcl, validateRoleUpdateAcl } from 'src/roles/roles.validations';

export default [
    {
        path: '/users',
        name: 'users',
        redirect: { name: 'userList' },
        component: RouterViewSlider,
        meta: {
            navigationGuard: () => validateRoleSelectAcl(),
            navbarTitle: messages.users.titles.nav,
            navbarIcon: 'mdi-account',
            transition: 'horizontal',
            featureFlags: [],
        },
        children: [
            {
                name: 'userList',
                path: '',
                component: () => import(/* webpackChunkName: "users" */ './UsersView.vue'),
                meta: {
                    pageBannerTitle: messages.users.titles.pageBannerTitle,
                    pageBannerSubtitle: messages.users.titles.pageBannerSubtitle,
                },
            },
            entityDetailRoute<'user'>({
                path: ':userId',
                name: 'userMaintenance',
                component: RouteEntityDetail,
                redirect: to => ({ name: 'userSettings', params: to.params }),
                meta: {
                    returnRoute: (route: Route) => ({ name: 'userList', query: route.query }),
                    canEdit: () => (accessor.entity?.entity as EntityTypeMap['user']).username !== 'applet' && validateRoleUpdateAcl(),
                    entityType: 'user',
                    getEntity: async (to) => {
                        if (to.query.mode === 'create') {
                            return {
                                entity: {
                                    description: '',
                                    enabled: true,
                                    firstName: '',
                                    id: to.params.userId,
                                    lastName: '',
                                    userContexts: isAdmin()
                                        ? []
                                        : [ { id: uuidv4(), context_id: accessor.auth.contextId } ],
                                    userRoles: [],
                                    username: '',
                                    localAuth: {
                                        id: '',
                                        email: '',
                                        password: '',
                                        currentPassword: '',
                                    },
                                    userAccessTokens: [],
                                    createdBy: null,
                                    updatedBy: null,
                                },
                            };
                        } else {
                            const { user: [ user ] } = await gqlClient.request({
                                document: GetUserDocument,
                                variables: {
                                    queryFilter: {
                                        id: {
                                            _eq: to.params.userId,
                                        },
                                    },
                                },
                            });

                            if (!user) {
                                throw new Error('Could not find user');
                            }

                            return {
                                entity: userFragmentToEntityType(user),
                            };
                        }
                    },
                    createEntity: createUserEntity,
                    saveEntity: saveUserEntity,
                    pageBannerTitle: () => entityPageBannerTitle('user', userToString(accessor.entityAsType('user'))),
                },
                children: [
                    {
                        path: 'settings',
                        name: 'userSettings',
                        component: () => import(/* webpackChunkName: "users" */ './UserSettingsView.vue'), // a component for the settings page content
                        meta: {
                            tab: { // the presence of this prop indicates this is a tab
                                label: 'Settings',
                                icon: 'mdi-cog',
                            },
                        },
                    },
                ],
            }),
        ],
    },
];
